import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text, Button, Input } from 'v3/components';
import { cooperplaceApi } from 'services/api';
import { useLocation, useParams } from 'react-router-dom';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { styles } from './styles';
import Card from './Card';
import { Select } from 'v3/components';
import {
  maskPhone,
  maskCpfCnpj,
  formatSouthAmericaPhone,
  maskDDI,
  maskInternationalDocument,
  getInternationalDocPlaceholder,
} from './Utils/masks';
import { validatePersonalDataDetailed } from './Utils/validators';
import {
  safeStr,
  getInputValue,
  isAnswered,
  hasBackAttachments,
  hasLocalFiles,
  isAttachmentRequiredByBackend,
} from './Utils/helper';
import {
  isParentQuestion,
  shouldShowSubQuestion,
  groupQuestionsWithSubQuestions,
} from 'v3/pages/Checklist/CheckListSingle/utils/questionTree';
import { collectFilesToUpload, appendFilesToFormData } from 'v3/pages/Checklist/CheckListSingle/utils/collectFilesToUpload';

function processParentQuestion(question, allQuestions) {
  const subQuestion = (allQuestions || []).find((q) => q.fatherQuestionId === question.id);
  if (!subQuestion) return question;
  const triggerValueIds = new Set(
    (question.questionSubResponses || [])
      .map((item) => item?.questionValueId)
      .filter(Boolean)
  );
  const processedResponses = (question.responses || []).map((response) => {
    const links = Array.isArray(response?.subValueResponse) ? response.subValueResponse : [];
    const triggersSubQuestion = links.some((link) => {
      return (
        link?.questionResponseId === subQuestion.fatherQuestionId ||
        link?.questionResponseId === subQuestion.id
      );
    });
    return {
      ...response,
      triggerSubQuestion:
        response?.triggerSubQuestion ||
        triggersSubQuestion ||
        (response?.id ? triggerValueIds.has(response.id) : false),
    };
  });
  return {
    ...question,
    responses: processedResponses,
    questionTrigger: processedResponses.filter((r) => r.triggerSubQuestion),
  };
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < breakpoint);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  return isMobile;
}

function useColumns() {
  const [w, setW] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (w < 768) return 1;
  if (w < 1200) return 2;
  return 3;
}

function FullscreenLoading({ open, text = 'Enviando...' }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #E5E7EB',
          padding: 16,
          minWidth: 260,
          fontWeight: 800,
          color: '#111827',
          textAlign: 'center',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        }}
      >
        {text}
      </div>
    </div>
  );
}

function normalizeShowPayload(data) {
  const payload = data?.data || data;
  if (!payload) return null;

  const raw =
    payload?.questionsResponses ||
    payload?.questions ||
    payload?.data?.questionsResponses ||
    payload?.data?.questions ||
    [];

  const flattened =
    Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0]?.questions)
      ? raw.flatMap((g) =>
        (g.questions || []).map((q) => ({
          ...q,
          groupedQuestion: g.group ?? q.groupedQuestion ?? 1,
        }))
      )
      : raw;

  const list = Array.isArray(flattened)
    ? [...flattened]
      .map((q) => ({
        ...q,
        attachment: q.attachment || {},
        questionDescriptiveResponse: q.questionDescriptiveResponse ?? q.descriptiveResponse ?? '',
        responses: Array.isArray(q.responses) ? q.responses : [],
        attachments: Array.isArray(q.attachments) ? q.attachments : [],
      }))
    : [];

  const anonymous =
    !!payload?.singleChecklistConfig?.anonymous ||
    !!payload?.singleChecklistConfig?.isAnonymous ||
    !!payload?.link?.isAnonymous ||
    !!payload?.isAnonymous;

  return {
    ...payload,
    singleChecklistConfig: { ...(payload?.singleChecklistConfig || {}), anonymous },
    questionsResponses: list,
  };
}

function getUniqueLinkLockInfo(payload) {
  const link = payload?.link || {};
  const cfg = payload?.singleChecklistConfig || {};

  const isUnique =
    !!link?.responseUnique ||
    !!cfg?.responseUnique ||
    !!cfg?.singleUse ||
    !!payload?.responseUnique ||
    !!payload?.singleUse;

  if (!isUnique) return { blocked: false, reason: '' };

  const answeredAt = payload?.answeredAt || payload?.answered_at || link?.answeredAt || link?.answered_at;

  const responsesCount =
    Number(payload?.responsesCount ?? link?.responsesCount ?? payload?.meta?.responsesCount ?? payload?.responseCount ?? 0) ||
    0;

  const anyAnsweredInQuestions =
    Array.isArray(payload?.questionsResponses) && payload.questionsResponses.some((q) => !!q?.answeredAt);

  const consumed = responsesCount >= 1 || !!answeredAt || anyAnsweredInQuestions;

  if (consumed) {
    return { blocked: true, reason: 'Esse link já foi respondido.' };
  }

  return { blocked: false, reason: '' };
}

export default function PublicChecklistResponsePage() {
  const location = useLocation();
  const { token: tokenParam } = useParams();
  const isMobile = useIsMobile(768);
  const cols = useColumns();

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useMemo(() => new URLSearchParams(location.search), [location.search]);

  const token = tokenParam;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const submitInFlightRef = useRef(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [model, setModel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState('form');

  const [person, setPerson] = useState({
    firstName: '',
    lastName: '',
    email: '',
    ddi: '55',
    ddd: '',
    phone: '',
    docType: 'cpfCnpj',
    docValue: '',
  });

  const [personErrors, setPersonErrors] = useState({});
  const [personTouched, setPersonTouched] = useState({});
  const [qTouched, setQTouched] = useState({});
  const [qErrors, setQErrors] = useState({});
  const [answered, setAnswered] = useState(false);
  const [submissionSucceeded, setSubmissionSucceeded] = useState(false);
  const [uniqueLock, setUniqueLock] = useState({ blocked: false, reason: '' });

  const locked = saving || answered || submissionSucceeded || uniqueLock.blocked;

  useEffect(() => {
    if (!submissionSucceeded || typeof window === 'undefined') return;

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [submissionSucceeded]);

  const touchPersonField = useCallback((key) => {
    setPersonTouched((t) => ({ ...t, [key]: true }));
  }, []);

  const hasPersonError = useCallback(
    (key) => !!personErrors?.[key] && !!personTouched?.[key],
    [personErrors, personTouched]
  );

  const inputErrorStyle = useCallback(
    (key) => (hasPersonError(key) ? { border: '1px solid #FCA5A5' } : undefined),
    [hasPersonError]
  );

  const errorTextStyle = useMemo(() => ({ marginTop: 6, color: '#DC2626', fontSize: 12, fontWeight: 700 }), []);

  const showQuestionError = useCallback((id) => !!qTouched?.[id] && !!qErrors?.[id], [qTouched, qErrors]);

  function touchQuestion(id) {
    setQTouched((t) => ({ ...t, [id]: true }));
  }

  function getQuestionError(q) {
    const required = !!q.required;

    if (required && !isAnswered(q)) return 'Campo obrigatório.';

    const attachmentRequired = isAttachmentRequiredByBackend(q);
    if (attachmentRequired && !hasBackAttachments(q) && !hasLocalFiles(q)) return 'Anexo obrigatório.';

    return '';
  }

  function validateAllQuestionsAndMark() {
    const nextErrors = {};
    const nextTouched = {};
    const grouped = groupQuestionsWithSubQuestions(questions);

    grouped.forEach(({ parent, subQuestions }) => {
      nextTouched[parent.id] = true;
      const err = getQuestionError(parent);
      if (err) nextErrors[parent.id] = err;

      const visibleSubs = subQuestions.filter((sq) => shouldShowSubQuestion(parent, sq));
      visibleSubs.forEach((sub) => {
        nextTouched[sub.id] = true;
        const subErr = getQuestionError(sub);
        if (subErr) nextErrors[sub.id] = subErr;
      });
    });

    setQTouched(nextTouched);
    setQErrors(nextErrors);

    return nextErrors;
  }

  const applyLoadedChecklist = useCallback((normalized) => {
    const lockInfo = getUniqueLinkLockInfo(normalized);
    setUniqueLock(lockInfo);
    setSubmissionSucceeded(false);

    const rawQuestions = normalized?.questionsResponses || [];
    const processedQuestions = rawQuestions.map((q) =>
      isParentQuestion(q) ? processParentQuestion(q, rawQuestions) : q
    );

    setModel(normalized);
    setQuestions(processedQuestions);

    if (lockInfo.blocked) {
      setStep('questions');
      setAnswered(true);
      setSuccessMsg(lockInfo.reason || 'Esse link já foi respondido.');
      setError('');
      setQTouched({});
      setQErrors({});
      return;
    }

    if (normalized?.singleChecklistConfig?.anonymous) setStep('questions');
    else setStep('form');

    setAnswered(false);
    setQTouched({});
    setQErrors({});
    setError('');
    setSuccessMsg('');
  }, []);

  const fetchPublicChecklist = useCallback(async () => {
    setError('');
    setSuccessMsg('');
    setSubmissionSucceeded(false);
    setLoading(true);

    if (!token || String(token).trim().length < 6) {
      setModel(null);
      setQuestions([]);
      setUniqueLock({ blocked: false, reason: '' });
      setError('Link inválido ou incompleto.');
      setLoading(false);
      return;
    }

    try {
      const { data } = await cooperplaceApi.get(`/checklist-link/questions/${token}`);
      const normalized = normalizeShowPayload(data);

      if (!mountedRef.current) return;

      if (!normalized) {
        setModel(null);
        setQuestions([]);
        setUniqueLock({ blocked: false, reason: '' });
        setError('Checklist não encontrado.');
      } else {
        applyLoadedChecklist(normalized);
      }
    } catch (e) {
      if (!mountedRef.current) return;

      const backMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        (Array.isArray(e?.response?.data?.messages) ? e.response.data.messages.join(' • ') : '') ||
        '';

      setModel(null);
      setQuestions([]);
      setUniqueLock({ blocked: false, reason: '' });
      setError(backMsg ? safeStr(backMsg) : 'Não foi possível carregar o checklist público.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [applyLoadedChecklist, token]);

  useEffect(() => {
    fetchPublicChecklist();
  }, [fetchPublicChecklist]);

  function updateQuestion(questionId, fn) {
    setQuestions((prev) => {
      const next = (prev || []).map((q) => {
        if (q.id !== questionId) return q;

        const cloned = {
          ...q,
          responses: Array.isArray(q.responses) ? q.responses.map((r) => ({ ...r })) : [],
          attachments: Array.isArray(q.attachments) ? [...q.attachments] : [],
          attachment: q.attachment ? { ...q.attachment } : {},
        };

        fn(cloned);
        return cloned;
      });

      return next;
    });
  }

  const removeSingleFile = useCallback((questionId, fileIndex) => {
    touchQuestion(questionId);

    updateQuestion(questionId, (q) => {
      const filesArr = Array.from(q?.attachment?.files || []);
      filesArr.splice(fileIndex, 1);

      q.attachment = {
        ...q.attachment,
        files: filesArr.length ? filesArr : null,
      };
    });
  }, []);

  function toggleUnique(questionId, optionId) {
    touchQuestion(questionId);
    updateQuestion(questionId, (q) => {
      q.responses = (q.responses || []).map((r) => ({
        ...r,
        selected: r.id === optionId,
      }));
    });
  }

  function toggleMultiple(questionId, optionId) {
    touchQuestion(questionId);
    updateQuestion(questionId, (q) => {
      q.responses = (q.responses || []).map((r) => {
        if (r.id !== optionId) return r;
        return { ...r, selected: !r.selected };
      });
    });
  }

  function setDescriptive(questionId, value) {
    touchQuestion(questionId);
    updateQuestion(questionId, (q) => {
      q.questionDescriptiveResponse = value;
    });
  }

  function setUploadFiles(questionId, files) {
    touchQuestion(questionId);
    updateQuestion(questionId, (q) => {
      q.attachment = { files, questionId: q.id };
      q.error = null;
      q.mediaError = null;
    });
  }

  function setPersonField(key, opts = {}) {
    return (arg) => {
      const raw = getInputValue(arg);
      let next = raw;

      if (opts.mask === 'ddi') next = maskDDI(raw);
      if (opts.mask === 'phone') next = maskPhone(raw);
      if (opts.mask === 'cpfCnpj') next = maskCpfCnpj(raw);
      if (opts.mask === 'internationalDoc') next = maskInternationalDocument(raw, opts.ddi);

      setPerson((prev) => {
        const nextPerson = { ...prev, [key]: next };

        if (key === 'ddi' && maskDDI(next) === '55') {
          nextPerson.docType = 'cpfCnpj';
          nextPerson.docValue = '';
        }

        setPersonErrors((prevErr) => {
          if (!personTouched?.[key]) return prevErr;
          const nextErrors = validatePersonalDataDetailed(nextPerson);
          return { ...prevErr, [key]: nextErrors?.[key] };
        });

        return nextPerson;
      });
    };
  }

  const isAnonymous = !!model?.singleChecklistConfig?.anonymous;
  const headerTitle = model?.name || 'Responder Checklist';
  const groupedQuestions = useMemo(() => groupQuestionsWithSubQuestions(questions), [questions]);
  const total = groupedQuestions.length;

  const answeredCount = useMemo(() => {
    return groupedQuestions.reduce((acc, { parent, subQuestions }) => {
      const parentAnswered = isAnswered(parent);
      const visibleSubs = subQuestions.filter((sq) => shouldShowSubQuestion(parent, sq));
      const subsOk = visibleSubs.every((sq) => !sq.required || isAnswered(sq));
      return acc + (parentAnswered && subsOk ? 1 : 0);
    }, 0);
  }, [groupedQuestions]);
  const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  async function handleNextFromForm() {
    setError('');
    setSuccessMsg('');

    const touchedAll = { firstName: true, lastName: true, email: true, ddi: true, phone: true, docType: true, docValue: true };
    setPersonTouched(touchedAll);

    const errors = validatePersonalDataDetailed(person);
    setPersonErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError('Revise os campos obrigatórios.');
      return;
    }

    setStep('questions');
  }

  function buildLinkResponsePayload({ questions: qs, person: p, isAnonymous: anon, uploadIds }) {
    const payloadQuestions = (qs || []).map((q) => {
      const hasLocal = uploadIds.has(q.id);
      const hasBack = hasBackAttachments(q);
      const desc = safeStr(q.questionDescriptiveResponse).trim();

      return {
        id: q.id,
        question: safeStr(q.question),
        required: q.required ?? null,
        needAttachment: !!q.needAttachment,
        sentAttachment: hasLocal || hasBack ? true : false,
        questionDescriptiveResponse: desc ? desc : null,
        type: { id: q?.type?.id, name: q?.type?.name, slug: q?.type?.slug },
        responses: (q.responses || []).map((r) => ({
          id: r.id,
          selected: r.selected ?? null,
          description: safeStr(r.description),
        })),
      };
    });

    const payload = { questions: payloadQuestions };

    if (!anon) {
      payload.name = `${safeStr(p.firstName).trim()} ${safeStr(p.lastName).trim()}`.trim();

      const ddi = safeStr(p.ddi).trim() || '55';
      const rawPhone = safeStr(p.phone).trim();
      payload.phoneNumber = formatSouthAmericaPhone(rawPhone, ddi);
      payload.email = safeStr(p.email).trim();

      if (p.docType === 'cpfCnpj') {
        payload.cpfCnpj = safeStr(p.docValue).trim();
      } else {
        payload.internationalDocument = safeStr(p.docValue).trim();
        payload.documentType = 'international';
      }
    }

    return payload;
  }

  async function handleSubmit() {
    if (!model) return;
    if (answered) return;
    if (saving) return;
    if (submitInFlightRef.current) return;
    if (uniqueLock.blocked) return;

    setError('');
    setSuccessMsg('');

    if (!token || String(token).trim().length < 6) {
      setError('Link inválido.');
      return;
    }

    if (!isAnonymous && step !== 'questions') return;

    if (!isAnonymous) {
      const touchedAll = { firstName: true, lastName: true, email: true, ddi: true, phone: true, docType: true, docValue: true };
      setPersonTouched(touchedAll);

      const errors = validatePersonalDataDetailed(person);
      setPersonErrors(errors);

      if (Object.keys(errors).length > 0) {
        setError('Revise os campos obrigatórios.');
        return;
      }
    }

    const errs = validateAllQuestionsAndMark();
    if (Object.keys(errs).length > 0) {
      setError('Revise os campos obrigatórios.');
      return;
    }

    submitInFlightRef.current = true;
    setSaving(true);

    try {
      // Respostas e anexos vão juntos: o backend grava tudo na mesma transação,
      // então uma falha não deixa anexos gravados para serem duplicados no reenvio.
      const entries = collectFilesToUpload(groupedQuestions);
      const uploadIds = new Set(entries.map((entry) => entry.questionId));
      const payload = buildLinkResponsePayload({ questions, person, isAnonymous, uploadIds });

      if (entries.length) {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(payload));
        appendFilesToFormData(formData, entries);
        await cooperplaceApi.post(`/checklist-link/response/${token}`, formData);
      } else {
        await cooperplaceApi.post(`/checklist-link/response/${token}`, payload);
      }

      setAnswered(true);
      setSubmissionSucceeded(true);
    } catch (e) {
      const backMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        (Array.isArray(e?.response?.data?.messages) ? e.response.data.messages.join(' • ') : '') ||
        '';

      setError(backMsg ? safeStr(backMsg) : 'Não foi possível enviar as respostas do checklist.');
    } finally {
      submitInFlightRef.current = false;
      setSaving(false);
    }
  }

  const containerStyle = useMemo(
    () => ({
      ...(styles.container || {}),
      maxWidth: '100%',
      width: '100%',
      paddingLeft: isMobile ? 12 : 24,
      paddingRight: isMobile ? 12 : 24,
    }),
    [isMobile]
  );

  const gridStyle = useMemo(() => {
    const base = typeof styles.grid === 'function' ? styles.grid(isMobile) : styles.grid || {};
    return {
      ...base,
      display: 'grid',
      width: '100%',
      gap: isMobile ? 12 : 16,
      alignItems: 'stretch',
      gridTemplateColumns:
        cols === 1 ? '1fr' : cols === 2 ? 'repeat(2, minmax(320px, 1fr))' : 'repeat(3, minmax(320px, 1fr))',
    };
  }, [isMobile, cols]);

  const topWrapStyle = useMemo(
    () => ({
      backgroundColor: '#0B2A4A',
      width: '100%',
      position: 'sticky',
      top: 0,
      zIndex: 9999,
      borderBottom: 'none',
      boxShadow: 'none',
    }),
    []
  );

  const docTypeOptions = useMemo(() => {
    const base = [{ value: 'cpfCnpj', label: 'CPF/CNPJ (Brasil)' }];
    if (maskDDI(person.ddi) !== '55') {
      base.push({ value: 'international', label: 'Documento internacional' });
    }
    return base;
  }, [person.ddi]);

  const docTypeSelected = useMemo(
    () => docTypeOptions.find((o) => o.value === person.docType) || null,
    [docTypeOptions, person.docType]
  );

  return (
    <>
      <FullscreenLoading open={saving} text="Enviando respostas..." />

      <div style={topWrapStyle}>
        <div style={styles.topInner(isMobile)}>
          <img src="/images/Logo-Cooperplace-Branca.png" alt="CooperPlace" style={styles.logo} draggable={false} />
        </div>

        <div style={styles.headerArea(isMobile)}>
          <div style={styles.headerRow(isMobile)}>
            <div style={{ minWidth: 0 }}>
              <div style={styles.headerTitle}>{headerTitle}</div>
              <div style={styles.headerSub}>{isAnonymous ? 'Resposta anônima' : 'Resposta identificada'}</div>
            </div>

            <div style={styles.counterPill}>
              {answeredCount} de {total} respondidas
            </div>
          </div>

          <div style={styles.progressTrack}>
            <div style={styles.progressFill(pct)} />
          </div>
        </div>
      </div>

      <div style={styles.page}>
        <div style={containerStyle}>
          <div style={styles.body}>
            {submissionSucceeded ? (
              <div
                style={styles.successScreen(isMobile)}
                role="status"
                aria-live="polite"
              >
                <div style={styles.successCard(isMobile)}>
                  <div style={styles.successIcon} aria-hidden="true">
                    <CheckCircleOutlineIcon style={{ fontSize: 44 }} />
                  </div>

                  <Text type="header" color="dark" style={styles.successTitle(isMobile)}>
                    Checklist respondido com sucesso!
                  </Text>

                  <Text color="gray" style={styles.successDescription(isMobile)}>
                    Sua resposta foi enviada e registrada.
                  </Text>
                </div>
              </div>
            ) : (
              <>
                {!!error && (
                  <div style={styles.messageWrap('error')}>
                    <div style={styles.messageBox('error', cols, isMobile)}>
                      <Text color="danger">{error}</Text>
                    </div>
                  </div>
                )}

                {!!successMsg && (
                  <div style={styles.messageWrap('success')}>
                    <div style={styles.messageBox('success', cols, isMobile)}>
                      <Text color="success">{successMsg}</Text>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div style={styles.messageWrap('info')}>
                    <div style={styles.messageBox('info', cols, isMobile)}>
                      <Text>Carregando checklist...</Text>
                    </div>
                  </div>
                ) : !model ? (
                  error ? null : (
                    <div style={styles.messageWrap('info')}>
                      <div style={styles.messageBox('info', cols, isMobile)}>
                        <Text color="gray">Checklist não encontrado.</Text>
                      </div>
                    </div>
                  )
                ) : uniqueLock.blocked ? null : (
                  <>
                    {!isAnonymous && step === 'form' && (
                      <div style={styles.card}>
                        <Text type="header" color="dark" style={{ fontWeight: 800 }}>
                          Identificação
                        </Text>

                        <div style={styles.formGrid(isMobile)}>
                          <div>
                            <Text type="small" color="gray">
                              Nome
                            </Text>
                            <Input
                              value={person.firstName}
                              onChange={setPersonField('firstName')}
                              onBlur={() => touchPersonField('firstName')}
                              placeholder="Seu nome"
                              style={inputErrorStyle('firstName')}
                              disabled={locked}
                            />
                            {hasPersonError('firstName') && <div style={errorTextStyle}>* {personErrors.firstName}</div>}
                          </div>

                          <div>
                            <Text type="small" color="gray">
                              Sobrenome
                            </Text>
                            <Input
                              value={person.lastName}
                              onChange={setPersonField('lastName')}
                              onBlur={() => touchPersonField('lastName')}
                              placeholder="Seu sobrenome"
                              style={inputErrorStyle('lastName')}
                              disabled={locked}
                            />
                            {hasPersonError('lastName') && <div style={errorTextStyle}>* {personErrors.lastName}</div>}
                          </div>

                          <div>
                            <Text type="small" color="gray">
                              E-mail
                            </Text>
                            <Input
                              value={person.email}
                              onChange={setPersonField('email')}
                              onBlur={() => touchPersonField('email')}
                              placeholder="seuemail@exemplo.com"
                              style={inputErrorStyle('email')}
                              disabled={locked}
                            />
                            {hasPersonError('email') && <div style={errorTextStyle}>* {personErrors.email}</div>}
                          </div>

                          <div>
                            <Text type="small" color="gray">
                              Telefone
                            </Text>

                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <div style={{ width: 90 }}>
                                <Input
                                  value={person.ddi}
                                  onChange={setPersonField('ddi', { mask: 'ddi' })}
                                  onBlur={() => touchPersonField('ddi')}
                                  placeholder="DDI"
                                  style={inputErrorStyle('ddi')}
                                  disabled={locked}
                                />
                              </div>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Input
                                  value={person.phone}
                                  onChange={setPersonField('phone', { mask: 'phone' })}
                                  onBlur={() => touchPersonField('phone')}
                                  placeholder="(xx) xxxxx-xxxx"
                                  style={inputErrorStyle('phone')}
                                  disabled={locked}
                                />
                              </div>
                            </div>

                            {(hasPersonError('ddi') || hasPersonError('phone')) && (
                              <div style={errorTextStyle}>
                                * {personErrors.ddi || personErrors.phone}
                              </div>
                            )}
                          </div>

                          <div>
                            <Text type="small" color="gray">  Tipo de documento </Text>
                            <Select
                              options={docTypeOptions}
                              value={docTypeSelected}
                              placeholder="Selecione..."
                              isDisabled={locked}
                              disableSearch
                              disableClear
                              error={hasPersonError('docType') ? personErrors.docType : null}
                              onBlur={() => touchPersonField('docType')}
                              onChange={(opt) => {
                                const nextType = opt?.value || 'cpfCnpj';

                                setPerson((prev) => ({
                                  ...prev,
                                  docType: nextType,
                                  docValue: '',
                                }));

                                touchPersonField('docType');
                              }}
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                              }}
                              menuStyle={{ zIndex: 999999 }}
                              id="docType"
                            />

                            {hasPersonError('docType') && <div style={errorTextStyle}>* {personErrors.docType}</div>}
                          </div>

                          <div>
                            <Text type="small" color="gray">
                              {person.docType === 'cpfCnpj' ? 'CPF/CNPJ' : 'Documento internacional'}
                            </Text>
                            <Input
                              value={person.docValue}
                              onChange={setPersonField('docValue', {
                                mask: person.docType === 'cpfCnpj' ? 'cpfCnpj' : 'internationalDoc',
                                ddi: person.ddi,
                              })}
                              onBlur={() => touchPersonField('docValue')}
                              placeholder={
                                person.docType === 'cpfCnpj'
                                  ? '000.000.000-00 / AA.AAA.AAA/AAAA-00'
                                  : getInternationalDocPlaceholder(person.ddi)
                              }
                              style={inputErrorStyle('docValue')}
                              disabled={locked}
                            />

                            {hasPersonError('docValue') && <div style={errorTextStyle}>* {personErrors.docValue}</div>}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <Button onClick={handleNextFromForm} disabled={locked}>
                              Continuar
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {(isAnonymous || step === 'questions') && (
                      <div style={gridStyle}>
                        {groupedQuestions.map(({ parent, subQuestions }, idx) => {
                          const visibleSubs = subQuestions.filter((sq) => shouldShowSubQuestion(parent, sq));

                          return (
                            <div
                              key={parent.id}
                              style={{
                                background: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: 12,
                                padding: isMobile ? 10 : 14,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                              }}
                            >
                              <Card
                                q={parent}
                                idx={idx}
                                locked={locked}
                                errVisible={showQuestionError(parent.id)}
                                qErrors={qErrors}
                                touchQuestion={touchQuestion}
                                onToggleUnique={toggleUnique}
                                onToggleMultiple={toggleMultiple}
                                onSetDescriptive={setDescriptive}
                                onSetUpload={setUploadFiles}
                                onRemoveFile={removeSingleFile}
                              />

                              {!!visibleSubs.length && (
                                <div
                                  style={{
                                    borderTop: '1px dashed #E5E7EB',
                                    paddingTop: 10,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    background: '#F9FAFB',
                                    borderRadius: 8,
                                    padding: 10,
                                  }}
                                >
                                  {visibleSubs.map((subQ, subIdx) => (
                                    <Card
                                      key={subQ.id}
                                      q={subQ}
                                      idx={`${idx + 1}.${subIdx + 1}`}
                                      locked={locked}
                                      errVisible={showQuestionError(subQ.id)}
                                      qErrors={qErrors}
                                      touchQuestion={touchQuestion}
                                      onToggleUnique={toggleUnique}
                                      onToggleMultiple={toggleMultiple}
                                      onSetDescriptive={setDescriptive}
                                      onSetUpload={setUploadFiles}
                                      onRemoveFile={removeSingleFile}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {!!model && !loading && !submissionSucceeded && !uniqueLock.blocked && (isAnonymous || step === 'questions') && (
            <div style={styles.footer}>
              <div style={styles.leftFooter}>
                {answered ? (
                  <div style={styles.missing}>Checklist respondido.</div>
                ) : (
                  <div style={styles.missing}>Faltam {total - answeredCount} pergunta(s)</div>
                )}
              </div>

              <div style={styles.rightFooter}>
                {!isAnonymous && step === 'questions' && !answered && (
                  <Button
                    onClick={() => setStep('form')}
                    disabled={locked}
                    outlined
                  >
                    <Text type="small" weight={800}>
                      Voltar
                    </Text>
                  </Button>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={locked || total === 0 || answered || uniqueLock.blocked}
                  loading={saving}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {answered ? (
                      <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                    ) : (
                      <SendOutlinedIcon style={{ fontSize: 18 }} />
                    )}

                    <Text type="small" weight={800}>
                      {answered ? 'Respondido' : 'Responder'}
                    </Text>
                  </div>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
